import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';

interface QuantityBarProps {
    /** Accessible name, e.g. "Vanguard to scrap". */
    label: string;
    value: number;
    /** Upper bound when one exists. Omitted where the ceiling is a budget rather than a count (the Recovery Center). */
    max?: number;
    /** Filled width, 0-100. Callers compute this — the fill is not always `value / max` (scrap cost isn't linear in quantity). */
    pct: number;
    /** Tailwind background class for the fill, e.g. `bg-status-danger/60`. */
    fillClassName: string;
    disabled?: boolean;
    /**
     * Absolute set from a click or Home/End, given the 0-1 position along the
     * bar. Callers map the fraction to a quantity themselves, since what the
     * bar's width represents differs per page.
     */
    onSetFraction: (fraction: number) => void;
    /** Relative nudge from the arrow keys, mirroring the flanking −/+ buttons. */
    onStep: (delta: number) => void;
    /** Optional overlay marks drawn between the fill and the centred value. */
    children?: ReactNode;
}

/**
 * The shared quantity bar behind the Recovery Center's and the population
 * lock's ship pickers: click a position to jump straight to that quantity,
 * or drive it from the keyboard once focused. Both pages flank it with −/+
 * steppers wired to `useHoldRepeat`, and both keep their own
 * fraction-to-quantity math — only the widget itself is shared.
 */
export default function QuantityBar({
    label,
    value,
    max,
    pct,
    fillClassName,
    disabled = false,
    onSetFraction,
    onStep,
    children,
}: QuantityBarProps) {
    function handleClick(e: MouseEvent<HTMLDivElement>) {
        if (disabled) return;
        const rect = e.currentTarget.getBoundingClientRect();
        onSetFraction(Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)));
    }

    function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
        if (disabled) return;
        switch (e.key) {
            case 'ArrowRight':
            case 'ArrowUp':
                onStep(1);
                break;
            case 'ArrowLeft':
            case 'ArrowDown':
                onStep(-1);
                break;
            case 'Home':
                onSetFraction(0);
                break;
            case 'End':
                onSetFraction(1);
                break;
            default:
                return;
        }
        e.preventDefault();
    }

    return (
        <div
            className={`flex-1 h-6 rounded-full bg-surface-void border border-hairline overflow-hidden relative focus:outline-none focus-visible:ring-2 focus-visible:ring-status-info ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            role="slider"
            tabIndex={disabled ? -1 : 0}
            aria-label={label}
            aria-valuenow={value}
            aria-valuemin={0}
            aria-valuemax={max}
            aria-orientation="horizontal"
            aria-disabled={disabled || undefined}
            title="Click or use the arrow keys to set quantity"
        >
            <div className={`h-full pointer-events-none ${fillClassName}`} style={{ width: `${pct}%` }} />
            {children}
            <span className="absolute inset-0 flex items-center justify-center text-value text-content-primary pointer-events-none">
                {value.toLocaleString()}
            </span>
        </div>
    );
}
